import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AlertMessageService, LoadingService, User, UserService } from '@serpro/ngx-suite-rfb';
import { appRoute } from 'src/app/shared/consts/app-routes';
import { Anotacao } from 'src/app/shared/model/anotacao.model';
import { Assunto } from 'src/app/shared/model/assunto.model';
import { AtoComparacao } from 'src/app/shared/model/ato-comparacao.model';
import { AtoConsulta } from 'src/app/shared/model/ato-consulta.model';
import { Ato } from 'src/app/shared/model/ato.model';
import { Orgao } from 'src/app/shared/model/orgao.model';
import { Segmento, SegmentoTela } from 'src/app/shared/model/segmento.model';
import { TipoAto } from 'src/app/shared/model/tipo-ato.model';
import { TipoEdicao } from 'src/app/shared/model/tipo-edicao.model';
import { TipoSegmento } from 'src/app/shared/model/tipo-segmento.model';
import { AtoService } from 'src/app/shared/services/ato.service';
import { CompartilhamentoDadosService } from 'src/app/shared/services/compartilhamento-dados.service';
import { DataUtil } from 'src/app/shared/util/data.util';
import { QuillHtmlUtil } from 'src/app/shared/util/quill-html.util';
import { SegmentoTelaUtil } from 'src/app/shared/util/segmento-tela.util';

/**
 * Este componente exibe o formulário de edição de atos para as funcionalidades:
 *  - incluir-ato
 *  - incluir-por-curadoria
 *  - incluir-por-duplicacao
 *  - corrigir-ato
 *  - republicar-ato
 *
 * Parte de sua complexidade é implementada no EditarAtoComponent (carga inicial e salvamento).
 *
 * Inclui o EditarConteudoAtoComponent, que implementa a edição rica do conteúdo textual do ato.
 */
@Component({
    selector: 'app-formulario-ato',
    templateUrl: './formulario-ato.component.html',
    styleUrls: ['./formulario-ato.component.css', '../../../../../src/app/shared/css/contraste.css']
})
export class FormularioAtoComponent implements OnInit {

    @Input() tipoEdicao: TipoEdicao;
    @Input() ato: Ato;
    @Input() tiposAto: TipoAto[];
    @Input() orgaos: Orgao[];
    @Input() tiposSegmento: TipoSegmento[];
    @Input() assuntos: Assunto[];
    @Input() anotacoes: Anotacao[];
    @Input() segmentosTela: SegmentoTela[];
    @Output() salvar: EventEmitter<Ato> = new EventEmitter<Ato>();
    @Output() limpar: EventEmitter<boolean> = new EventEmitter<boolean>();

    textoAtoARepublicar: string;
    altObservacaoAtoRepublicacao: string;

    public readonly = true;

    public usuarioLogado: User;
    public observacaoOriginal: string;

    public conteudoAtoOriginal: string;

    public labelsTipoAto!: string[];

    public labelsOrgao!: string[];
    public idsOrgaos!: number[];
    public orgaosSelecionados: Orgao[] = [];

    public labelsAssunto: string[];
    public assunto: Assunto;
    public assuntoAnterior: Assunto;

    public labelsTipoSegmento!: string[];

    public visualizarObservacoes = false;
    public segmentosCarregados: boolean;
    public fullscreen = false;

    atosDuplicados: AtoConsulta[] = [];
    textoAlertaDuplicidade = '';
    converterEmPublicacaoRepetida = false;
    exibirVisoesAntesDeConverterPublicacaoRepetica: string;

    constructor(
        private loadingService: LoadingService,
        private alertMessageService: AlertMessageService,
        private router: Router,
        private userService: UserService,
        private compartilhamentoDadosService: CompartilhamentoDadosService,
        private atoService: AtoService,
    ) { }

    ngOnInit(): void {
        this.readonly = false;

        this.segmentosTela.forEach(s =>
            s.textoIntegra = QuillHtmlUtil.removerEstilosMapeados(s.textoIntegra));

        // obtém o usuário logado);
        this.userService.getUser().subscribe( (user) => this.usuarioLogado = user );

        // prepara a tela
        this.labelsTipoAto = this.tiposAto.map(o => o.nomeTipoAto);
        this.labelsOrgao = this.orgaos.map(o => o.siglaOrgao + ' - ' + o.nomeOrgao);
        this.idsOrgaos = this.orgaos.map(o => o.idOrgao);
        this.labelsTipoSegmento = this.tiposSegmento.map(o => o.nomeTipoSegmento);
        this.assunto = this.assuntos[0];
        this.assuntoAnterior = this.assuntos[0];
        this.labelsAssunto = this.assuntos.map(a => a.nomeAssunto);

        if (this.ato.tipoAto) {
            const t = this.tiposAto.find(t => t.idTipoAto == this.ato.tipoAto.idTipoAto);
            if (t) {
                this.ato.tipoAto = t;
            }
        }

        if (!this.tipoEdicao.isRepublicacaoAto()) {
            this.observacaoOriginal = this.ato.observacao;
            if (this.observacaoOriginal) {
                this.visualizarObservacoes = true;
            }
        }

        if (this.tipoEdicao.isInclusaoAto()) {
            this.segmentosCarregados = false;
        } else {
            this.orgaosSelecionados = [];
            for (const orgao of this.ato.orgaos) {
                const achado = this.orgaos.find(o => o.idOrgao == orgao.idOrgao);
                if (achado) {
                    this.orgaosSelecionados.push(achado);
                }
            }
            this.segmentosCarregados = true;
        }

        if (this.tipoEdicao.isRepublicacaoAto()) {
            this.segmentosCarregados = false;
            this.verificarAtosDuplicados();
            this.ajustarRepublicacao();
        }

        if (this.tipoEdicao.isCorrecaoAto()) {
            this.verificarAtosDuplicados();
        }

        if (this.tipoEdicao.isRepublicacaoAtoComparacao()) {
            const atoComparacao: AtoComparacao = this.compartilhamentoDadosService.getData("repub-comparacao");
            if (atoComparacao.assunto) {
                const assuntoEncontrado = this.assuntos.find(a => a.idAssunto == atoComparacao.assunto.idAssunto);
                if (assuntoEncontrado) {
                    this.assunto = assuntoEncontrado;
                }
            }
            this.conteudoAtoOriginal = atoComparacao.conteudoAtoOriginal;
            this.segmentosCarregados = atoComparacao.segmentosCarregados;
            this.ajustarRepublicacao();
        }
    }

    /********* MÉTODOS ESPECÍFICOS DOS CAMPOS DO FORMULÁRIO - OBJETO this.ato */

    public compareOrgaos = (p1: Orgao, p2: Orgao): boolean => {
        return p1 && p2 ? p1.idOrgao === p2.idOrgao : p1 === p2;
    }

    private ajustarRepublicacao() {
        this.textoAtoARepublicar = "Ato a republicar: " + this.ato.epigrafeCompleta + " ";
        if (this.ato.observacao) {
            this.altObservacaoAtoRepublicacao = this.ato.observacao;
            this.ato.observacao = "";
        } else {
            this.altObservacaoAtoRepublicacao = "Nenhuma observação cadastrada no ato republicado";
        }
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    public onChangeLocal(event: any) {
        this.ato.idLocalPublicacao = parseInt(event.srcElement.value);
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    public onChangePublicado(event: any) {
        // os radio buttons Publicado e Não Publicado devem atualizar o valor boolean de this.ato.publicado
        this.ato.publicado = ('Publicado' === event.srcElement.value);
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    public onChangeVisao(event: any, indice: number) {
        // os checkboxes das Visões formam uma sequência de 4 bits, exemplo '0110', a armazenar como string em this.ato.exibirVisoes
        const bit = event.srcElement.checked ? '1' : '0';
        let s = this.ato.exibirVisoes;
        s = s.substring(0, indice) + bit + s.substring(indice + 1, 4);
        this.ato.exibirVisoes = s;
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    public onChangeInternet(event: any) {
        // o checkbox de Exibição na Internet deve ser armazenado como booleano em this.ato.internet
        this.ato.internet = event.srcElement.checked;
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    onChangeExibicaoSijut(event: any) {
        // o checkbox de Exibição no SIJUT-1 deve ser armazenado como '0' ou '1' em this.ato.flags
        this.ato.flags = event.srcElement.checked ? '1' : '0';
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars */
    onClickAssunto(event: any) {
        if (this.assuntoAnterior != this.assunto) {
            for (const s of this.segmentosTela) {
                s.idAssunto = this.assunto.idAssunto;
            }
            this.assuntoAnterior = this.assunto;
        }
    }

    /********* Transformar Conteúdo do Ato em texto puro para Segmentos */

    public prosseguir() {

        this.alertMessageService.dismissAll();

        if (!this.conteudoAtoOriginal || this.conteudoAtoOriginal.length < 1) {
            this.alertMessageService.warn('Validação', 'O campo Conteúdo do ato deve ser preenchido.');
            return;
        }

        this.loadingService.show();
        setTimeout(() => {
            this.gerarConteudo();
        }, 200);
    }

    private gerarConteudo() {
        this.segmentosTela = [];
        const paragrafos = this.conteudoAtoOriginal.split('\n');

         for (const paragrafo of paragrafos) {
            if (paragrafo.trim().length > 0) {
                const segmento = new SegmentoTela();
                segmento.idTipoSegmento = 0;
                segmento.idAssunto = this.assunto.idAssunto;
                segmento.textoIntegra = paragrafo;
                this.segmentosTela.push(segmento);
            }
        }
        this.transformarConteudoAtoEmSegmentos();
        this.segmentosCarregados = true;
        this.loadingService.hide();
    }

    private transformarConteudoAtoEmSegmentos() {
        let indice = 0;
        for (const segmento of this.segmentosTela) {
            if (segmento.idTipoSegmento == 0) {
                segmento.idTipoSegmento = 999; //NAO INDENTIFICADO(ainda)
                if (indice == 0) {
                    segmento.idTipoSegmento = 1; //EMENTA
                } else if (indice == this.segmentosTela.length - 1) {
                    segmento.idTipoSegmento = 4; //FECHO
                } else {
                    if (new RegExp('^(Par[áÁaA]grafo(s?)( )|(§))', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 5; //PARAGRAFO
                    }
                    if (new RegExp('^[a-z][)]( )', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 14; //ALINEA
                    }
                    if (new RegExp('^(([0-9])+[\\.])+( )', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 6; //ITEM
                    }
                    if (new RegExp('^Subse[çÇ]([ãÃ]o|[õÕ]es)', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 8; //SUBSECAO
                    }
                    if (new RegExp('^(Art\\.\\s)', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 3; //ARTIGO
                    }
                    if (new RegExp('^M?M?M?M?(CM|CD|D?C?C?C?)(XC|XL|L?X?X?X?)(IX|IV|V?I?I?I?)( )', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 15; //INCISO
                    }
                    if (new RegExp('^Se[çÇ]([ãÃ]o|[õÕ]es)', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 9; //SECAO
                    }
                    if (new RegExp('^Cap[íÍiI]tulo(s?)( )', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 10; //CAPITULO
                    }
                    if (new RegExp('^T[íÍiI]tulo(s?)( )', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 11; //TITULO
                    }
                    if (new RegExp('^Livro(s?)( )', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 12; //LIVRO
                    }
                    // if (new RegExp('^Anexo(s?)( )', 'i').test(segmento.textoIntegra)) {
                    //     segmento.idTipoSegmento = 16; //ANEXO
                    // }
                    if (new RegExp('^Parte(s?)( )', 'i').test(segmento.textoIntegra)) {
                        segmento.idTipoSegmento = 13; //PARTE
                    }
                }
            }
            indice++;
        }
    }

    /********* Ajuste de Observações do Ato */

    public exibirObservacoes(): void {
        this.visualizarObservacoes = true;
    }

    public inibirObservacoes(): void {
        this.visualizarObservacoes = false;
    }

    /********* Operações SALVAR e CANCELAR */

    public limparFormulario() {
        this.limpar.emit(true);
    }

    private validarInclusaoAto(): boolean {

        if (!this.ato.tipoAto) {
            this.alertMessageService.warn('Validação', 'O campo Tipo do ato deve ser preenchido.');
            return false;
        }
        if (!this.ato.numeroAto) {
            this.alertMessageService.warn('Validação', 'O campo Número do ato deve ser preenchido.');
            return false;
        }
        if (!this.ato.dataAto) {
            this.alertMessageService.warn('Validação', 'O campo Data do ato deve ser preenchido.');
            return false;
        }
        if (!this.orgaosSelecionados || this.orgaosSelecionados.length < 1) {
            this.alertMessageService.warn('Validação', 'O campo Órgãos / unidades deve ser preenchido.');
            return false;
        }
        if (!this.ato.dataPublicacao) {
            this.alertMessageService.warn('Validação', 'O campo Data da publicação deve ser preenchido.');
            return false;
        }
        if (this.segmentosTela.length == 0) {
            this.alertMessageService.warn('Validação', 'O conteúdo do ato não pode estar vazio.');
            return false;
        }

        return true;
    }

    public salvarAto() {

        this.alertMessageService.dismissAll();

        DataUtil.zerarHora(this.ato.dataAto);
        DataUtil.zerarHora(this.ato.dataPublicacao);
        DataUtil.zerarHora(this.ato.dataVigenciaInicio);
        DataUtil.zerarHora(this.ato.dataVigenciaFim);

        // Validações
        if (!this.validarInclusaoAto()) {
            return;
        }

        this.loadingService.show();
        // this.ato.files = [];
        const segmentosFinais: Segmento[] = [];
        let i = 1;

        for (const s of this.segmentosTela) {
            const seg = SegmentoTelaUtil.obterSegmentoParaSalvar(s);
            seg.ordemSegmentoAto = i++;
            segmentosFinais.push(seg);
            if (s.anexoInconsistente) {
                this.alertMessageService.error('Erro', 'Há ao menos um segmento com inconsistência de anexo, destacado em vermelho. Corrija a inconsistência antes de continuar.');
                this.loadingService.hide();
                return;
            }
        }

        this.ato.segmentos = segmentosFinais;
        this.ato.orgaos = this.orgaosSelecionados;

        if (this.ato.observacao != this.observacaoOriginal) {
            this.ato.observacao += ' - ' + DataUtil.dateAndTimeToStr(new Date()) + ' (' + (this.usuarioLogado ? this.usuarioLogado.name : 'não logado') + ')';
        }

        this.salvar.emit(this.ato);
    }

    public verificarAtosDuplicados() {

        if (!this.tipoEdicao.isInclusaoAto() && !this.tipoEdicao.isCorrecaoAto()
            && !this.tipoEdicao.isCuradoriaAto() && !this.tipoEdicao.isDuplicacaoAto()) {
            return;
        }

        // Se epígrafe preenchida...
        if (this.ato && this.ato.tipoAto && this.ato.numeroAto && this.ato.dataAto && this.orgaosSelecionados && this.orgaosSelecionados.length > 0) {

            this.atoService.buscarPorEpigrafe(this.ato, this.orgaosSelecionados).subscribe({
                next: retorno => {
                    this.atosDuplicados = retorno;

                    if (this.atosDuplicados.length > 0) {

                        // Remove o próprio ato (usado na Correção do Ato)
                        this.atosDuplicados = this.atosDuplicados.filter(a => a.idAto !== this.ato.idAto);

                        if (this.atosDuplicados.length == 1) {
                            this.textoAlertaDuplicidade = 'Foi encontrado 1 ato com a mesma epígrafe.';
                        } else {
                            this.textoAlertaDuplicidade = 'Foram encontrados ' + this.atosDuplicados.length + ' atos com a mesma epígrafe.';
                        }
                    }
                }
            });
        } else {
            this.atosDuplicados = [];
            this.textoAlertaDuplicidade = '';
        }
    }

    public getConteudoComparacao(): string {

        if (this.segmentosCarregados) {
            if (this.segmentosTela.length == 0) {
                return "";
            }
        } else {
            return this.conteudoAtoOriginal;
        }

        let conteudoComparacao = "";

        for (const s of this.segmentosTela) {

            if (s.idTipoSegmento == 16) {
                if (s.arquivoBinario.idTipoArquivo === 4 || s.arquivoBinario.idTipoArquivo === 5) {
                    conteudoComparacao += s.arquivoBinario.conteudoTabela +  "\n";
                } else {
                    conteudoComparacao += s.arquivoBinario.nomeArquivoBinario + " (" + s.arquivoBinario.tamanho + " bytes)\n";
                }
            } else {
                conteudoComparacao += s.textoIntegra + "\n";
            }
        }

        return conteudoComparacao;
    }

    rolarParaTabelaAtosDuplicados() {
        setTimeout(() => {
            const element = document.getElementById("div-atos-duplicados");
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 10);
    }

    public getIdAtoCorrecao(): number {
        if (this.tipoEdicao.isCorrecaoAto()) {
            return this.ato.idAto ? this.ato.idAto : 0;
        }
        return 0;
    }

    public prosseguirDaComparacao() {
        if (this.exibirVisoesAntesDeConverterPublicacaoRepetica) {
            this.ato.exibirVisoes = this.exibirVisoesAntesDeConverterPublicacaoRepetica;
        }
        this.prosseguir();
    }

    public republicarDaComparacao(idAtoOriginalRepublicacao: number) {

        // "Prosseguir"
        if (!this.segmentosCarregados) {
            this.gerarConteudo();
        }

        this.ato.orgaos = this.orgaosSelecionados;

        const atoComparacao = new AtoComparacao();
        atoComparacao.ato = this.ato;
        atoComparacao.idAtoOriginalRepublicacao = idAtoOriginalRepublicacao;
        atoComparacao.assunto = this.assunto;
        atoComparacao.conteudoAtoOriginal = this.conteudoAtoOriginal;
        atoComparacao.segmentosCarregados = this.segmentosCarregados;
        atoComparacao.segmentosTela = this.segmentosTela.map(st => st.clone());
        atoComparacao.segmentosTela.forEach(st => st.idSegmento = null);

        this.compartilhamentoDadosService.setData("repub-comparacao", atoComparacao);
        this.router.navigate([`${appRoute.ato.republicarComparacao}`]);
    }

    public converterPublicacaoRepetida(idAtoSelecionado: number) {

        this.converterEmPublicacaoRepetida = true;
        this.ato.idAtoAnteriorPublicacaoRepetida = idAtoSelecionado;

        this.loadingService.show();

        this.exibirVisoesAntesDeConverterPublicacaoRepetica = this.ato.exibirVisoes;

        this.atoService.buscarPorIdSemSegmentos(idAtoSelecionado).subscribe({
            next: ato => {

                this.ato.exibirVisoes = '1000';

                this.segmentosTela = [];

                const segmentoEmenta = new SegmentoTela();
                segmentoEmenta.idTipoSegmento = 0;
                segmentoEmenta.idAssunto = this.assunto.idAssunto;
                segmentoEmenta.textoIntegra = "Publicação repetida";
                this.segmentosTela.push(segmentoEmenta);

                const segmentoNota = new SegmentoTela();
                segmentoNota.idTipoSegmento = 0;
                segmentoNota.idAssunto = this.assunto.idAssunto;
                segmentoNota.textoIntegra = "Nota Normas: Já houve uma publicação deste ato no " +
                    ato.dadosPublicacaoRepetida +
                    ". Consulte a publicação anterior para tomar conhecimento do seu conteúdo e de eventuais modificações que tenha sofrido.";
                this.segmentosTela.push(segmentoNota);

                this.transformarConteudoAtoEmSegmentos();

                this.segmentosTela[this.segmentosTela.length - 1].idTipoSegmento = 999;

                this.segmentosCarregados = true;
                this.loadingService.hide();
            }
        });
    }
}
